import { ReactNode, useState } from 'react';
import { authApi } from '../authApi';
import { useSelector } from 'react-redux';
import { RootState } from '@/app/store';
import { authFailed, authSuccess, clearAuth } from '../authSlice';
import { toast } from "sonner"
import { handleErrorStandard } from '@/components/utils/helper';
import { AuthContext } from './AuthContext';
import { AuthRequest } from '../auth.interface';
import { systemSettingsApi } from '@/features/systems-settings/systems-settingsApi';
import { useAppDispatch } from '@/hooks/use-state';


export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  
  const [login] = authApi.useLoginMutation();
  const [logout] = authApi.useLogoutMutation();

  const loginHandler = async (credentials: AuthRequest) => {
    try {

      setIsLoading(true);

      const res = await login(credentials).unwrap();
      
      if (!res.success || !res.data) {
        const errorMessage = res.message || 'Error al iniciar sesión';
        dispatch(authFailed(errorMessage));
        toast.error(errorMessage);
        return;
      }


      // Acceder al token correctamente
      const accessToken = res.data.access_token;
      if (!accessToken) {
        dispatch(authFailed('Token de acceso no recibido'));
        toast.error('Error en la autenticación');
        return;
      }

      sessionStorage.setItem('access_token', accessToken);

      dispatch(authSuccess({user: res.data.payload.user, grupo: res.data.payload.grupo ,menu: res.data.menu}));

      // Prefetch de settings sin force para aprovechar cache RTK Query.
      try {
        dispatch(systemSettingsApi.util.prefetch("getSystemSettings", undefined, { force: false }));
      } catch (prefetchError) {
        console.error("System settings prefetch failed", prefetchError);
      }

      if(res.data.payload.user.needs_password_reset === true) setIsDefault(true);
      toast.success(`Bienvenido, ${res.data.payload.user?.email || 'Usuario'}`);

    } catch (error: unknown ) {

      const messageToUser = handleErrorStandard(error);
      dispatch(authFailed(messageToUser));
      toast.error(messageToUser);

    } finally {
      setIsLoading(false);
    }
  };

  const logoutHandler = async () => {
    try {
      await logout().unwrap();
      sessionStorage.removeItem('access_token');
      dispatch(clearAuth());
      toast.success(`Has cerrado sesión correctamente`);
    } catch (error: unknown) {
      const messageToUser = handleErrorStandard(error);
      toast.error(messageToUser);
    }
  };


  return (
    <AuthContext.Provider value={{ login: loginHandler, logout: logoutHandler, user, isDefault, setIsDefault, isLoading}}>
      {children}
    </AuthContext.Provider>
  );
};
